/**
 * The ONE place that talks to the API.
 *
 * The original project hardcoded http://127.0.0.1:5004 in nine different
 * files, which meant it could only ever run on the developer's laptop.
 * Base URL now comes from the environment, and in dev Vite proxies /api
 * to the backend so the browser sees same-origin requests.
 *
 * Tokens live in memory with a sessionStorage mirror. Memory is the source
 * of truth (so an XSS payload cannot simply read a global), and the mirror
 * only exists so a page refresh does not log the user out.
 */
import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios'

const BASE = import.meta.env.VITE_API_BASE_URL ?? ''

const ACCESS_KEY = 'bv.access'
const REFRESH_KEY = 'bv.refresh'

let accessToken: string | null = sessionStorage.getItem(ACCESS_KEY)
let refreshToken: string | null = sessionStorage.getItem(REFRESH_KEY)

type Listener = (authed: boolean) => void
const listeners = new Set<Listener>()

export function onAuthChange(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function emit(authed: boolean) {
  listeners.forEach((l) => l(authed))
}

export function setTokens(access: string, refresh: string) {
  accessToken = access
  refreshToken = refresh
  sessionStorage.setItem(ACCESS_KEY, access)
  sessionStorage.setItem(REFRESH_KEY, refresh)
  emit(true)
}

export function clearTokens() {
  accessToken = null
  refreshToken = null
  sessionStorage.removeItem(ACCESS_KEY)
  sessionStorage.removeItem(REFRESH_KEY)
  emit(false)
}

export function getAccessToken(): string | null {
  return accessToken
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE,
  timeout: 20000,
  headers: { 'Content-Type': 'application/json' },
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`
  return config
})

// --- refresh handling -------------------------------------------------
// When several requests fail with 401 at once, only ONE refresh call is
// made; the rest queue and replay with the new token. Without this, a
// screen that fires five parallel requests would trigger five refreshes
// and invalidate its own tokens in the race.

let refreshing: Promise<string | null> | null = null

/**
 * The company inside an access token, read from its payload.
 *
 * Only to tell the server which company to renew INTO. Nothing is trusted
 * on the strength of it -- the server re-checks access on every request --
 * so reading an unverified payload here is safe.
 */
export function tokenCompanyId(token: string | null): number | null {
  if (!token) return null
  try {
    const part = token.split('.')[1]
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    const cid = JSON.parse(json).cid
    return cid == null ? null : Number(cid)
  } catch {
    return null
  }
}

/*
 * Two bugs lived here, and the second was hiding behind the first.
 *
 * 1. The refresh token went as a query parameter with an empty body, and
 *    the server reads a JSON body. Every renewal failed with 422, the
 *    catch below signed the user out, and nobody noticed because an
 *    access token lasts a working day.
 *
 * 2. Fixing only that would have made renewal return a token for the
 *    user's DEFAULT company. Someone who had switched to the demo company
 *    would have been moved back to the real books mid-session, with the
 *    header still reading "Demo" -- figures from one company shown under
 *    the other's name.
 *
 * So: a JSON body, and the company the session is actually in.
 */
async function doRefresh(): Promise<string | null> {
  if (!refreshToken) return null
  try {
    const { data } = await axios.post(
      `${BASE}/api/v1/auth/refresh`,
      { refresh_token: refreshToken,
        company_id: tokenCompanyId(accessToken) },
    )
    setTokens(data.access_token, data.refresh_token)
    return data.access_token as string
  } catch {
    clearTokens()
    return null
  }
}

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & {
      _retried?: boolean
    }
    const status = error.response?.status

    const isAuthCall = original?.url?.includes('/auth/login') ||
      original?.url?.includes('/auth/refresh')

    if (status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true
      if (!refreshing) refreshing = doRefresh().finally(() => { refreshing = null })
      const fresh = await refreshing
      if (fresh) {
        original.headers.Authorization = `Bearer ${fresh}`
        return api(original)
      }
    }
    return Promise.reject(error)
  },
)

/** Turn an axios failure into a message worth showing a user. */
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)?.detail
    if (typeof detail === 'string') return detail
    // FastAPI validation errors arrive as a list of field problems.
    if (Array.isArray(detail)) {
      return detail
        .map((d: { loc?: (string | number)[]; msg?: string }) => {
          const field = d.loc?.filter((p) => p !== 'body').join('.')
          return field ? `${field}: ${d.msg}` : d.msg
        })
        .filter(Boolean)
        .join('; ')
    }
    if (err.code === 'ECONNABORTED') return 'The server took too long to respond.'
    if (!err.response) return 'Cannot reach the server. Is the API running?'
    return err.message
  }
  return err instanceof Error ? err.message : 'Unexpected error'
}
